import React, { useCallback, useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material//Clear';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { CustomUser } from 'api';
import { COMPANY, COMPANY_USER_VALUE } from 'common/constants/user';
import { useSaveUserPermissions } from 'services/hooks/useManageUser/useManageUser';
import { getUserPermissions } from 'services/hooks/useUserSelected';
import { Logger } from 'utilities';
import CompanyUsersTable from './CompanyUsersTable';
import { ManageCompanyUsersLedgerProps } from './types';
import UserDetailsDialog from './UserDetailsDialog';

const ManageCompanyUsersLedger = ({
  companyList,
  firmUsers,
  firmId,
  fundList,
  handleManageCompanyUsersDialogClose,
}: ManageCompanyUsersLedgerProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { mutateAsync: savePermissions, updatePermissionsForCompOrFundRole } = useSaveUserPermissions();
  const [emails, setEmails] = useState<{ [key: number]: string[] }>({});
  const [openUserDetailsDialog, setOpenUserDetailsDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newPendingUsers, setNewPendingUsers] = useState<{ email: string; companyId: number }[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const filteredCompanies = useMemo(
    () =>
      companyList.filter(company => {
        if (!company.company_name) {
          return false;
        }

        return company.company_name.toLowerCase().includes(searchTerm.toLowerCase());
      }),
    [companyList, searchTerm]
  );

  const getFormattedUsers = (emailList: string[], usersByFirm: CustomUser[]) =>
    emailList.map(email => {
      const user = usersByFirm.find(userFirm => userFirm.email === email);
      return {
        user_id: user?.id ?? null,
        user_email: email,
      };
    });

  const formatEmailsForSaving = useCallback(
    () =>
      Object.entries(emails).map(([companyId, emailList]) => ({
        company_id: Number(companyId),
        users: getFormattedUsers(emailList, firmUsers),
      })),
    [emails, firmUsers]
  );

  const handleSavePermissions = useCallback(async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const formattedEmails = formatEmailsForSaving();
    const tmpNewPendingUsers = formattedEmails.flatMap(({ company_id, users }) =>
      users.filter(user => user.user_id === null).map(user => ({ email: user.user_email, companyId: company_id }))
    );
    setNewPendingUsers(tmpNewPendingUsers);
    setOpenUserDetailsDialog(false);

    const existingUserPermissionSaves = formattedEmails.flatMap(({ company_id, users }) =>
      users.map(async user => {
        const userId = user.user_id;
        if (userId === null) {
          return;
        }

        try {
          let prevPermissions = await getUserPermissions(userId, firmId);
          if (!prevPermissions || !Array.isArray(prevPermissions.permissions)) {
            prevPermissions = { permissions: [] };
          }
          const userPermissionsToSave = updatePermissionsForCompOrFundRole(
            COMPANY,
            company_id,
            prevPermissions,
            COMPANY_USER_VALUE,
            firmId,
            userId
          );
          await savePermissions({
            userId,
            roleType: COMPANY_USER_VALUE,
            firmId,
            userPermissionsToSave,
          });
        } catch (error) {
          enqueueSnackbar(`Failed to save permissions for user ${user.user_email}`, { variant: 'error' });
          Logger.error(`Failed to save permissions for user ${user.user_email}`, error);
          throw error;
        }
      })
    );

    const hasNewPendingUsers = !isEmpty(tmpNewPendingUsers);

    try {
      await Promise.all(existingUserPermissionSaves);

      if (hasNewPendingUsers) {
        setOpenUserDetailsDialog(true);
        return;
      }

      enqueueSnackbar('User permissions saved successfully', { variant: 'success' });
      handleManageCompanyUsersDialogClose();
    } catch {
      // Individual failures are surfaced per user above.
    } finally {
      setIsSaving(false);
    }
  }, [
    formatEmailsForSaving,
    firmId,
    updatePermissionsForCompOrFundRole,
    savePermissions,
    handleManageCompanyUsersDialogClose,
    enqueueSnackbar,
    isSaving,
  ]);

  return (
    <>
      {!isEmpty(newPendingUsers) && (
        <UserDetailsDialog
          open={openUserDetailsDialog}
          onClose={() => setOpenUserDetailsDialog(false)}
          newPendingUsers={newPendingUsers}
          firmId={firmId}
          companyList={companyList}
          fundList={fundList}
          handleManageCompanyUsersDialogClose={handleManageCompanyUsersDialogClose}
        />
      )}
      <div style={{ overflow: 'hidden' }}>
        <TextField
          label="Search Companies"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <CompanyUsersTable
          filteredCompanies={filteredCompanies}
          firmUsers={firmUsers}
          emails={emails}
          setEmails={setEmails}
          firmId={firmId}
        />
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePermissions}
            disabled={isEmpty(emails) || isSaving}>
            Add Users
          </Button>
        </Box>
      </div>
    </>
  );
};

export default ManageCompanyUsersLedger;
